import { Janus } from 'janus-gateway';

// Must match LiveCall::SCREENSHARE_SUFFIX on the server.
export const SCREENSHARE_SUFFIX = "_screenshare";

// Capped at 720p to match the video rooms' H.264 profile (42e01f = level 3.1,
// max ~1280x720; see Janus#create_video_room). Higher resolutions can fail to
// decode for some viewers (e.g. Safari / hardware decoders).
const DISPLAY_MEDIA_OPTIONS = {
    video: {
        cursor: "always",
        width: { ideal: 1280, max: 1280 },
        height: { ideal: 720, max: 720 },
        frameRate: { ideal: 30, max: 30 }
    },
    audio: false
};

const PUBLISH_TIMEOUT_MS = 10000;

// Publishes a facilitator's screen into the session's Janus video room as a
// second publisher feed whose display name is "<contact_id>_screenshare".
//
// This class keeps its own plugin handle, because VideoConnectionController
// keeps single-publisher state (pluginHandle, mypvtid, resolvers) on `this`.
//
// States: idle -> starting -> active -> stopping -> idle
export default class ScreenshareConnection {
    constructor({ onStopped } = {}) {
        this.state = "idle";
        this.stream = null;
        this.pluginHandle = null;
        this.callObject = null;
        // Called once a share that had become active has fully stopped,
        // whatever stopped it (button, browser "Stop sharing", PeerConnection drop).
        this.onStopped = onStopped;
    }

    get isActive() {
        return this.state === "active";
    }

    get displayName() {
        return `${this.callObject.contactId}${SCREENSHARE_SUFFIX}`;
    }

    // Resolves once Janus confirms the screen is being published.
    // Rejects with a NotAllowedError if the user cancels the browser picker.
    async start(janus, callObject) {
        if (this.state !== "idle") {
            return;
        }
        if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
            throw new Error("Screen sharing is not supported in this browser");
        }
        if (!janus) {
            throw new Error("Not connected to the video room");
        }

        this.state = "starting";
        this.callObject = callObject;

        try {
            this.stream = await navigator.mediaDevices.getDisplayMedia(DISPLAY_MEDIA_OPTIONS);
            const track = this.stream.getVideoTracks()[0];
            if (!track) {
                throw new Error("No screen video track was captured");
            }
            // The browser's own "Stop sharing" control ends the track.
            // addEventListener (not onended) so VideoComponent's listener can't replace it.
            track.addEventListener('ended', () => this.stop());

            this.pluginHandle = await this.attach(janus);
            if (this.cancelled) throw new Error("Screen share cancelled");
            await this.ensureNobodyElseSharing();
            await this.join();
            await this.publish(track);

            this.state = "active";
            this.showOwnPreview();
        } catch (error) {
            // Clean up whatever we got as far as, so the browser's "sharing"
            // indicator doesn't stay on and the Janus handle doesn't leak.
            await this.teardown();
            this.state = "idle";
            if (this.cancelled) {
                // stop() was called mid-start (e.g. browser "Stop sharing"); not an error
                this.cancelled = false;
                return;
            }
            throw error;
        }
    }

    // Idempotent: safe to call from the button, track end, hangup and unload.
    async stop() {
        if (this.state === "idle" || this.state === "stopping") {
            return;
        }
        if (this.state === "starting") {
            // Let start() unwind: its next step fails on the torn-down handle
            this.cancelled = true;
            const cancelled = new Error("Screen share cancelled");
            if (this.rejectJoined) this.rejectJoined(cancelled);
            if (this.rejectPublished) this.rejectPublished(cancelled);
            await this.teardown();
            return;
        }
        const wasActive = this.state === "active";
        this.state = "stopping";
        await this.teardown();
        this.state = "idle";
        if (wasActive && this.onStopped) {
            this.onStopped();
        }
    }

    // --- internals ----------------------------------------------------------

    attach(janus) {
        return new Promise((resolve, reject) => {
            janus.attach({
                plugin: "janus.plugin.videoroom",
                opaqueId: "screenshare-" + Janus.randomString(12),
                success: (pluginHandle) => resolve(pluginHandle),
                error: (error) => reject(error),
                webrtcState: (on) => {
                    Janus.log("Screenshare PeerConnection is " + (on ? "up" : "down"));
                    if (on && this.resolvePublished) {
                        this.resolvePublished();
                    } else if (!on && this.state === "active") {
                        this.stop();
                    }
                },
                onmessage: (msg, jsep) => this.handleMessage(msg, jsep),
                onremotetrack: () => {
                    // Publisher handle is sendonly
                },
                oncleanup: () => {
                    Janus.log("Screenshare publisher cleaned up");
                }
            });
        });
    }

    // One share at a time: refuse if the room already has a screenshare feed.
    ensureNobodyElseSharing() {
        return new Promise((resolve, reject) => {
            this.pluginHandle.send({
                message: { request: "listparticipants", room: this.callObject.scheduled_session_id },
                success: (result) => {
                    const participants = (result && result.participants) || [];
                    const existing = participants.find(p => p.publisher === true && String(p.display).endsWith(SCREENSHARE_SUFFIX));
                    if (existing) {
                        reject(new Error("Someone else is already sharing their screen"));
                    } else {
                        resolve();
                    }
                },
                error: (error) => reject(error)
            });
        });
    }

    join() {
        return new Promise((resolve, reject) => {
            this.resolveJoined = resolve;
            this.rejectJoined = reject;
            this.pluginHandle.send({
                message: {
                    request: "join",
                    room: this.callObject.scheduled_session_id,
                    ptype: "publisher",
                    display: this.displayName
                },
                error: (error) => reject(error)
            });
        });
    }

    publish(track) {
        const published = new Promise((resolve, reject) => {
            this.resolvePublished = resolve;
            this.rejectPublished = reject;
        });
        const timeout = new Promise((_, reject) => {
            this.publishTimer = setTimeout(() => reject(new Error("Timed out publishing screen share")), PUBLISH_TIMEOUT_MS);
        });

        this.pluginHandle.createOffer({
            tracks: [{ type: 'video', capture: track, simulcast: false }],
            success: (jsep) => {
                this.pluginHandle.send({
                    message: { request: "configure", audio: false, video: true, data: false },
                    jsep: jsep,
                    error: (error) => this.rejectPublished && this.rejectPublished(error)
                });
            },
            error: (error) => this.rejectPublished && this.rejectPublished(error)
        });

        return Promise.race([published, timeout]).finally(() => {
            clearTimeout(this.publishTimer);
            this.resolvePublished = null;
            this.rejectPublished = null;
        });
    }

    handleMessage(msg, jsep) {
        const event = msg.videoroom;

        if (msg.error_code || msg.error) {
            const error = new Error(msg.error || `Janus error ${msg.error_code}`);
            if (this.rejectJoined) {
                this.rejectJoined(error);
                this.rejectJoined = null;
            } else if (this.rejectPublished) {
                this.rejectPublished(error);
            } else if (this.state === "active") {
                this.stop();
            }
            return;
        }

        if (event === "joined" && this.resolveJoined) {
            this.privateId = msg.private_id;
            this.resolveJoined();
            this.resolveJoined = null;
            this.rejectJoined = null;
        }

        if (jsep) {
            this.pluginHandle.handleRemoteJsep({ jsep: jsep });
            if (!msg.video_codec && this.rejectPublished) {
                this.rejectPublished(new Error("Screen share video was rejected by the server"));
            }
        }
    }

    // The sharer renders their own screen tile from the local capture rather
    // than subscribing to it through Janus (video_connection skips that feed).
    showOwnPreview() {
        window.remoteStreams = window.remoteStreams || {};
        window.remoteStreams[this.displayName] = this.stream;
        document.dispatchEvent(new CustomEvent('remoteStreamAdded', { detail: { remoteContactId: this.displayName } }));
    }

    async teardown() {
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }
        if (this.callObject && window.remoteStreams) {
            delete window.remoteStreams[this.displayName];
        }

        const handle = this.pluginHandle;
        this.pluginHandle = null;
        if (!handle) {
            return;
        }
        await new Promise((resolve) => {
            // Wait for Janus to acknowledge the leave (bounded), then detach
            let finished = false;
            const done = () => {
                if (finished) return;
                finished = true;
                clearTimeout(timer);
                handle.detach();
                resolve();
            };
            const timer = setTimeout(done, 2000);
            handle.send({
                message: { request: "leave" },
                success: done,
                error: done
            });
        });
    }
}
